import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';
import './UserValidator.css';

interface ValidateUserIdResponse {
  valid: boolean;
  userId: string;     // Public user ID
  sellerId: string;   // Auth0 user ID (internal)
  name: string;
  rating: number;
}

interface UserValidatorProps {
  userId: string;
  onValidationChange?: (isValid: boolean, publicUserId?: string, sellerId?: string, rating?: number) => void;
}

const UserValidator: React.FC<UserValidatorProps> = ({
  userId,
  onValidationChange
}) => {
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [validationMessage, setValidationMessage] = useState<string>('');
  const [isValidating, setIsValidating] = useState<boolean>(false);
  
  useEffect(() => {
    // Reset validation state when userId changes
    if (!userId) {
      setIsValid(null);
      setValidationMessage('');
      if (onValidationChange) onValidationChange(false, undefined, undefined, undefined);
      return;
    }
    
    // Debounce validation to avoid excessive API calls
    const timeoutId = setTimeout(async () => {
      setIsValidating(true);
      
      try {
        const response = await apiClient.users.validateUserId(userId) as ValidateUserIdResponse;
        setIsValid(true);
        setValidationMessage('Valid user ID');
        if (onValidationChange) onValidationChange(true, response.userId, response.sellerId, response.rating);
      } catch (error) {
        setIsValid(false);
        setValidationMessage('Invalid VeriPay ID');
        if (onValidationChange) onValidationChange(false, undefined, undefined, undefined);
      } finally {
        setIsValidating(false);
      }
    }, 500); // 500ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [userId, onValidationChange]);
  
  if (isValid === null) return null;
  
  return (
    <div className={`user-validator ${isValid ? 'valid' : 'invalid'}`}>
      {isValidating ? (
        <span className="validating-indicator">Validating...</span>
      ) : isValid ? (
        <span className="valid-indicator">✓</span>
      ) : (
        <span className="invalid-indicator">✗</span>
      )}
      <span className="validation-message">{validationMessage}</span>
    </div>
  );
};

export default UserValidator;